import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSocketEvents } from '../hooks/useSocket';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import HierarchicalUserCreation from '../components/HierarchicalUserCreation';
import BalanceMonitor from '../components/BalanceMonitor';
import ActivityHistory from '../components/ActivityHistory';
import ProspectManagement from '../components/ProspectManagement';
import SearchAndSort from '../components/SearchAndSort';
import ProfileSettings from '../components/ProfileSettings';
import AloriaLogo from '../components/AloriaLogo';
import UserManagement from '../components/UserManagement';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api, { visitorsAPI } from '../utils/api';

const SuperAdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [processingUserId, setProcessingUserId] = useState(null);

  // useCallback pour stabiliser les fonctions de callback
  const handleFilteredUsersChange = React.useCallback((data) => {
    setFilteredUsers(data);
  }, []);

  const handleFilteredActivitiesChange = React.useCallback((data) => {
    setFilteredActivities(data);
  }, []);

  const handleFilteredVisitorsChange = React.useCallback((data) => {
    setFilteredVisitors(data);
  }, []);

  const roleLabel = (role) => {
    if (!role) return '';
    switch (role) {
      case 'SUPERADMIN': return 'Super administrateur';
      case 'MANAGER': return 'Manager';
      case 'CONSULTANT': return 'Conseiller en immigration';
      case 'EMPLOYEE': return 'Employé';
      case 'CLIENT': return 'Client';
      default: return role;
    }
  };

  const handleToggleUserStatus = async (user) => {
    if (user.role === 'SUPERADMIN') {
      toast.error("Impossible de désactiver un SuperAdmin");
      return;
    }

    // Prevent duplicate requests for the same user
    if (processingUserId === user.id) return;

    setProcessingUserId(user.id);
    try {
      const nextStatus = !user.is_active;
      await api.patch(`/admin/users/${user.id}`, { is_active: nextStatus });
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_active: nextStatus } : u))
      );
      setFilteredUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_active: nextStatus } : u))
      );
      toast.success(`Utilisateur ${nextStatus ? 'activé' : 'désactivé'}`);
      // Rafraîchir les statistiques du dashboard
      await fetchDashboardData();
    } catch (error) {
      console.error('Error toggling user status', error);
      toast.error("Échec de la mise à jour du statut");
    } finally {
      setProcessingUserId(null);
    }
  };

  // Calcul dynamique des stats à partir des données locales pour mise à jour en temps réel
  const calculatedStats = React.useMemo(() => {
    // Filtrer uniquement les utilisateurs actifs pour correspondre au backend
    const activeUsers = users.filter(u => u.is_active);
    const managers = activeUsers.filter(u => u.role === 'MANAGER').length;
    const employeesCount = activeUsers.filter(u => u.role === 'EMPLOYEE').length;
    const clientsCount = activeUsers.filter(u => u.role === 'CLIENT').length;
    
    return {
      users: {
        total: activeUsers.length,
        managers: managers,
        employees: employeesCount,
        clients: clientsCount
      },
      business: stats?.business || {},
      consultations: stats?.consultations || { total_count: 0, total_amount: 0, currency: 'CFA' },
      activity: stats?.activity || {}
    };
  }, [users, stats]);

  // Utiliser les stats du serveur en priorité, sinon les stats calculées
  const displayStats = stats && Object.keys(stats).length > 0 ? stats : calculatedStats;

  useEffect(() => {
    fetchDashboardData();

    // Supprimé: Auto-refresh automatique qui causait des pertes de données
    // Les données seront mises à jour uniquement après les actions utilisateur

  }, []);

  // Écouter les mises à jour en temps réel via Socket.IO
  useSocketEvents('clientUpdated', (update) => {
    console.log('Client updated via Socket.IO:', update);
    fetchDashboardData();
  });

  useSocketEvents('newNotification', (notification) => {
    console.log('New notification via Socket.IO:', notification);
    // Recharger les stats si nécessaire
    fetchDashboardData();
  });

  // NOTE: Pas besoin d'initialiser filteredUsers/filteredActivities ici
  // car SearchAndSort le fait automatiquement via son useEffect

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, activitiesRes, visitorsRes] = await Promise.all([
        api.get('/admin/dashboard-stats'),
        api.get('/admin/users'),
        api.get('/admin/activities'),
        visitorsAPI.getAll()
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setVisitors(visitorsRes.data || []);
      setFilteredVisitors(visitorsRes.data || []);
      
      console.log('📊 Activities loaded:', activitiesRes.data.length, 'activities');
      console.log('📊 First 3 activities:', activitiesRes.data.slice(0, 3));
      setActivities(activitiesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await api.post(`/admin/impersonate`, { target_user_id: userId });
      const { impersonation_token, target_user } = response.data;

      // Save the original SuperAdmin token to restore later
      const originalToken = localStorage.getItem('token');
      if (originalToken) {
        localStorage.setItem('original_admin_token', originalToken);
      }

      // Replace the active token so AuthContext uses the impersonation one
      localStorage.setItem('token', impersonation_token);
      localStorage.setItem('token_timestamp', Date.now().toString());
      localStorage.setItem('impersonating', 'true');
      localStorage.setItem('user_role', target_user.role);
      localStorage.setItem('user_id', target_user.id);

      toast.success(`Impersonnification de ${target_user.name} réussie`);

      // Redirect to the correct role-based dashboard
      const rolePaths = {
        SUPERADMIN: '/superadmin/dashboard',
        MANAGER: '/manager/dashboard',
        ACCOUNTANT: '/accountant/dashboard',
        CONSULTANT: '/consultant/dashboard',
        EMPLOYEE: '/employee/dashboard',
        CLIENT: '/client/dashboard',
      };
      window.location.href = rolePaths[target_user.role] || '/';
    } catch (error) {
      toast.error('Erreur lors de l\'impersonnification');
      console.error('Error impersonating user:', error);
    }
  };

  const StatsCard = ({ title, value, icon, color, subtitle }) => (
    <div className="bg-slate-700 rounded-lg p-6 border border-slate-600">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-slate-500 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className={`${color} opacity-20`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const UsersTab = () => {
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [deleteDialog, setDeleteDialog] = useState({ show: false, user: null, confirmText: '' });
    const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ show: false, confirmText: '' });
    const [deleting, setDeleting] = useState(false);

    const toggleSelectUser = (userId) => {
      setSelectedUsers(prev => {
        const next = new Set(prev);
        if (next.has(userId)) next.delete(userId);
        else next.add(userId);
        return next;
      });
    };

    const toggleSelectAll = () => {
      const selectableUsers = filteredUsers.filter(u => u.role !== 'SUPERADMIN');
      if (selectedUsers.size === selectableUsers.length && selectableUsers.length > 0) {
        setSelectedUsers(new Set());
      } else {
        setSelectedUsers(new Set(selectableUsers.map(u => u.id)));
      }
    };

    const handleDeleteUser = async () => {
      const target = deleteDialog.user;
      if (!target) return;
      const expectedName = target.full_name?.split(' ')[0]?.toLowerCase();
      if (deleteDialog.confirmText.toLowerCase() !== expectedName) {
        toast.error(`Veuillez saisir "${target.full_name?.split(' ')[0]}" pour confirmer`);
        return;
      }
      setDeleting(true);
      try {
        await api.delete(`/admin/users/${target.id}`);
        setUsers(prev => prev.filter(u => u.id !== target.id));
        setFilteredUsers(prev => prev.filter(u => u.id !== target.id));
        setSelectedUsers(prev => { const n = new Set(prev); n.delete(target.id); return n; });
        toast.success(`Utilisateur "${target.full_name}" supprimé`);
        setDeleteDialog({ show: false, user: null, confirmText: '' });
        await fetchDashboardData();
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Erreur lors de la suppression');
      } finally {
        setDeleting(false);
      }
    };

    const handleBulkDelete = async () => {
      if (bulkDeleteDialog.confirmText !== 'SUPPRIMER') {
        toast.error('Veuillez saisir "SUPPRIMER" pour confirmer');
        return;
      }
      setDeleting(true);
      try {
        const res = await api.post('/admin/users/bulk-delete', { user_ids: Array.from(selectedUsers) });
        toast.success(res.data.message);
        if (res.data.skipped_count > 0) {
          toast.info(`${res.data.skipped_count} SuperAdmin(s) ignoré(s)`);
        }
        setSelectedUsers(new Set());
        setBulkDeleteDialog({ show: false, confirmText: '' });
        await fetchDashboardData();
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Erreur lors de la suppression en masse');
      } finally {
        setDeleting(false);
      }
    };

    const selectableCount = filteredUsers.filter(u => u.role !== 'SUPERADMIN').length;
    const allSelected = selectableCount > 0 && selectedUsers.size === selectableCount;

    return (
      <div className="bg-slate-700 rounded-lg p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Gestion des Utilisateurs</h3>
            <div className="flex items-center space-x-3">
              {selectedUsers.size > 0 && (
                <button
                  onClick={() => setBulkDeleteDialog({ show: true, confirmText: '' })}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center space-x-2"
                >
                  <span>🗑️</span>
                  <span>Supprimer ({selectedUsers.size})</span>
                </button>
              )}
              <span className="text-slate-400 text-sm">{filteredUsers.length} utilisateur(s)</span>
            </div>
          </div>

          <SearchAndSort
            data={users}
            searchFields={['full_name', 'email', 'role', 'phone', 'country']}
            sortOptions={[
              { value: 'created_at', label: 'Date de création' },
              { value: 'full_name', label: 'Nom' },
              { value: 'role', label: 'Rôle' },
              { value: 'email', label: 'Email' }
            ]}
            onFilteredDataChange={handleFilteredUsersChange}
            placeholder="Rechercher (nom, email, rôle, téléphone, pays)..."
          />
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-600">
                <th className="pb-3 pr-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-500 accent-orange-500"
                  />
                </th>
                <th className="pb-3 text-slate-300 font-medium">Utilisateur</th>
                <th className="pb-3 text-slate-300 font-medium">Rôle</th>
                <th className="pb-3 text-slate-300 font-medium">Contact</th>
                <th className="pb-3 text-slate-300 font-medium">Statut</th>
                <th className="pb-3 text-slate-300 font-medium">Inscrit le</th>
                <th className="pb-3 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className={`border-b border-slate-600/50 ${selectedUsers.has(u.id) ? 'bg-orange-500/10' : ''}`}>
                  <td className="py-4 pr-2">
                    {u.role !== 'SUPERADMIN' ? (
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(u.id)}
                        onChange={() => toggleSelectUser(u.id)}
                        className="w-4 h-4 rounded border-slate-500 accent-orange-500"
                      />
                    ) : <span className="w-4 h-4 block" />}
                  </td>
                  <td className="py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {u.full_name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <span className="text-white font-medium">{u.full_name || 'Nom non défini'}</span>
                    </div>
                  </td>
                  <td className="py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      u.role === 'SUPERADMIN' ? 'bg-red-500/20 text-red-400' :
                      u.role === 'MANAGER' ? 'bg-blue-500/20 text-blue-400' :
                      u.role === 'ACCOUNTANT' ? 'bg-purple-500/20 text-purple-400' :
                      u.role === 'CONSULTANT' ? 'bg-teal-500/20 text-teal-400' :
                      u.role === 'EMPLOYEE' ? 'bg-green-500/20 text-green-400' :
                      'bg-orange-500/20 text-orange-400'
                    }`}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="py-4">
                    <div className="text-slate-300 text-sm">{u.email}</div>
                    {u.phone && <div className="text-slate-400 text-xs">{u.phone}</div>}
                  </td>
                  <td className="py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      u.is_active 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {u.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="py-4 text-slate-300 text-sm">
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString('fr-FR')
                      : '-'
                    }
                  </td>
                  <td className="py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => { setSelectedUser(u); setShowUserDetails(true); }}
                        className="px-3 py-1 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded transition-colors"
                      >
                        Voir
                      </button>
                      <button
                        onClick={() => handleToggleUserStatus(u)}
                        disabled={processingUserId === u.id || u.role === 'SUPERADMIN'}
                        className={`px-3 py-1 text-sm rounded transition-colors ${
                          u.role === 'SUPERADMIN' ? 'bg-slate-600 text-slate-400 cursor-not-allowed' :
                          u.is_active
                            ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        } ${processingUserId === u.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        {processingUserId === u.id ? '…' : (u.is_active ? 'Désactiver' : 'Activer')}
                      </button>
                      {u.role !== 'SUPERADMIN' && (
                        <button
                          onClick={() => setDeleteDialog({ show: true, user: u, confirmText: '' })}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-slate-400">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Single delete confirmation dialog */}
        <Dialog open={deleteDialog.show} onOpenChange={(open) => { if (!open) setDeleteDialog({ show: false, user: null, confirmText: '' }); }}>
          <DialogContent className="bg-[#1E293B] border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">⚠️ Confirmer la suppression</DialogTitle>
              <DialogDescription className="text-slate-400">
                Cette action désactivera définitivement le compte de cet utilisateur.
              </DialogDescription>
            </DialogHeader>
            {deleteDialog.user && (
              <div className="space-y-4 py-4">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-white font-medium">{deleteDialog.user.full_name}</p>
                  <p className="text-slate-400 text-sm">{deleteDialog.user.email}</p>
                  <p className="text-slate-400 text-sm">{roleLabel(deleteDialog.user.role)}</p>
                </div>
                <div>
                  <label className="text-slate-300 text-sm block mb-2">
                    Pour confirmer, saisissez le prénom de l'utilisateur : <strong className="text-orange-400">{deleteDialog.user.full_name?.split(' ')[0]}</strong>
                  </label>
                  <Input
                    value={deleteDialog.confirmText}
                    onChange={(e) => setDeleteDialog(prev => ({ ...prev, confirmText: e.target.value }))}
                    placeholder={deleteDialog.user.full_name?.split(' ')[0]}
                    className="bg-slate-800 border-slate-600 text-white"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleDeleteUser(); }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setDeleteDialog({ show: false, user: null, confirmText: '' })}
                    className="flex-1 bg-slate-700 hover:bg-slate-600"
                    disabled={deleting}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleDeleteUser}
                    disabled={deleting || deleteDialog.confirmText.toLowerCase() !== deleteDialog.user.full_name?.split(' ')[0]?.toLowerCase()}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleting ? 'Suppression...' : 'Supprimer'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk delete confirmation dialog */}
        <Dialog open={bulkDeleteDialog.show} onOpenChange={(open) => { if (!open) setBulkDeleteDialog({ show: false, confirmText: '' }); }}>
          <DialogContent className="bg-[#1E293B] border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">⚠️ Suppression en masse</DialogTitle>
              <DialogDescription className="text-slate-400">
                Vous êtes sur le point de supprimer {selectedUsers.size} utilisateur(s).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 max-h-40 overflow-y-auto">
                {Array.from(selectedUsers).map(id => {
                  const u = users.find(x => x.id === id);
                  return u ? (
                    <div key={id} className="flex justify-between items-center py-1">
                      <span className="text-white text-sm">{u.full_name}</span>
                      <span className="text-slate-400 text-xs">{roleLabel(u.role)}</span>
                    </div>
                  ) : null;
                })}
              </div>
              <div>
                <label className="text-slate-300 text-sm block mb-2">
                  Pour confirmer, saisissez <strong className="text-red-400">SUPPRIMER</strong>
                </label>
                <Input
                  value={bulkDeleteDialog.confirmText}
                  onChange={(e) => setBulkDeleteDialog(prev => ({ ...prev, confirmText: e.target.value }))}
                  placeholder="SUPPRIMER"
                  className="bg-slate-800 border-slate-600 text-white"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleBulkDelete(); }}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setBulkDeleteDialog({ show: false, confirmText: '' })}
                  className="flex-1 bg-slate-700 hover:bg-slate-600"
                  disabled={deleting}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleBulkDelete}
                  disabled={deleting || bulkDeleteDialog.confirmText !== 'SUPPRIMER'}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? 'Suppression...' : `Supprimer ${selectedUsers.size} utilisateur(s)`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const ActivitiesTab = () => {
    const getActivityIcon = (action) => {
      switch (action) {
        case 'LOGIN': return '🔐';
        case 'CREATE_USER': return '👤';
        case 'CREATE_CLIENT': return '🤝';
        case 'PAYMENT_CONFIRMED': return '💰';
        case 'PAYMENT_DECLARED': return '💳';
        case 'CASE_UPDATED': return '📂';
        case 'CASE_CREATED': return '📋';
        case 'WITHDRAWAL_DECLARED': return '💸';
        case 'RESPOND_TO_CONTACT': return '📧';
        default: return '⚡';
      }
    };

    const getActivityColor = (action) => {
      switch (action) {
        case 'LOGIN': return 'bg-blue-500/20 text-blue-400';
        case 'CREATE_USER': 
        case 'CREATE_CLIENT': return 'bg-green-500/20 text-green-400';
        case 'PAYMENT_CONFIRMED': 
        case 'PAYMENT_DECLARED': return 'bg-orange-500/20 text-orange-400';
        case 'CASE_UPDATED':
        case 'CASE_CREATED': return 'bg-purple-500/20 text-purple-400';
        case 'WITHDRAWAL_DECLARED': return 'bg-red-500/20 text-red-400';
        default: return 'bg-slate-500/20 text-slate-400';
      }
    };

    return (
      <div className="bg-[#1E293B] border border-slate-600 rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white">Activités Récentes</h3>
          <button
            onClick={fetchDashboardData}
            className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded transition-colors"
          >
            🔄 Actualiser
          </button>
        </div>
        
        {activities.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <span className="text-4xl mb-2 block">📊</span>
            <p>Aucune activité récente</p>
          </div>
        ) : (
          <>
            {/* Recherche et Tri */}
            <SearchAndSort
              data={activities}
              searchFields={['user_name', 'action', 'details']}
              sortOptions={[
                { value: 'timestamp', label: 'Date' },
                { value: 'user_name', label: 'Utilisateur' },
                { value: 'action', label: 'Action' }
              ]}
              onFilteredDataChange={handleFilteredActivitiesChange}
              placeholder="Rechercher une activité (utilisateur, action, détails)..."
            />

            <div className="space-y-3">
              {filteredActivities.map((activity, index) => (
              <div key={activity.id || index} className="flex items-start space-x-4 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700/70 transition-colors">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActivityColor(activity.action)}`}>
                  <span className="text-lg">{getActivityIcon(activity.action)}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium">
                        {activity.user_name || activity.user_id}
                      </p>
                      <p className="text-slate-300 text-sm mt-1">
                        {activity.action === 'LOGIN' && 'Connexion au système'}
                        {activity.action === 'CREATE_USER' && `Création d'utilisateur ${activity.details?.role || ''}`}
                        {activity.action === 'CREATE_CLIENT' && 'Création d\'un nouveau client'}
                        {activity.action === 'PAYMENT_CONFIRMED' && `Paiement confirmé (${activity.details?.amount || 'N/A'} CFA)`}
                        {activity.action === 'PAYMENT_DECLARED' && `Paiement déclaré (${activity.details?.amount || 'N/A'} CFA)`}
                        {activity.action === 'CASE_UPDATED' && 'Mise à jour de dossier'}
                        {activity.action === 'CASE_CREATED' && 'Création de dossier'}
                        {activity.action === 'WITHDRAWAL_DECLARED' && `Retrait déclaré (${activity.details?.amount || 'N/A'} CFA)`}
                        {activity.action === 'RESPOND_TO_CONTACT' && 'Réponse à un prospect'}
                        {!['LOGIN', 'CREATE_USER', 'CREATE_CLIENT', 'PAYMENT_CONFIRMED', 'PAYMENT_DECLARED', 'CASE_UPDATED', 'CASE_CREATED', 'WITHDRAWAL_DECLARED', 'RESPOND_TO_CONTACT'].includes(activity.action) && activity.action}
                      </p>
                      {activity.details && (
                        <div className="text-xs text-slate-400 mt-2">
                          {activity.details.client_email && <span>Client: {activity.details.client_email} • </span>}
                          {activity.details.resource_type && <span>Type: {activity.details.resource_type} • </span>}
                          {activity.resource_id && <span>ID: {activity.resource_id.slice(0, 8)}...</span>}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-xs text-slate-400">
                      <div>{new Date(activity.timestamp).toLocaleDateString('fr-FR')}</div>
                      <div>{new Date(activity.timestamp).toLocaleTimeString('fr-FR')}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </>
        )}
        
        {activities.length > 0 && (
          <div className="text-center mt-4">
            <button
              onClick={() => window.open('/admin/activities', '_blank')}
              className="text-orange-400 hover:text-orange-300 text-sm underline"
            >
              Voir toutes les activités →
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <AloriaLogo />
            <span className="text-slate-400">SuperAdmin Console</span>
          </div>
          
          <div className="flex items-center space-x-4">
            <NotificationBell />
            <div className="flex items-center space-x-2">
              <span className="text-white font-medium">{user?.full_name}</span>
              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">SUPERADMIN</span>
            </div>
            <button
              onClick={() => setShowProfileSettings(!showProfileSettings)}
              className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <span>👤</span>
              <span>Mon Profil</span>
            </button>
            <button
              onClick={logout}
              className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <span>🚪</span>
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="p-6">
         {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <StatsCard
                title="Total Utilisateurs"
                value={displayStats?.users?.total || 0}
                icon={<span className="text-2xl">👥</span>}
                color="text-blue-400"
              />
              <StatsCard
                title="Managers"
                value={displayStats?.users?.managers || 0}
                icon={<span className="text-2xl">👨‍💼</span>}
                color="text-green-400"
              />
              <StatsCard
                title="Employés"
                value={displayStats?.users?.employees || 0}
                icon={<span className="text-2xl">👨‍💻</span>}
                color="text-yellow-400"
              />
              <StatsCard
                title="Clients"
                value={displayStats?.users?.clients || 0}
                icon={<span className="text-2xl">👥</span>}
                color="text-orange-400"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <StatsCard
                title="Total Dossiers"
                value={displayStats?.business?.total_cases || 0}
                icon={<span className="text-2xl">📂</span>}
                color="text-purple-400"
              />
              <StatsCard
                title="Paiements Confirmés"
                value={displayStats?.business?.total_payments || 0}
                icon={<span className="text-2xl">💰</span>}
                color="text-green-400"
              />
              <StatsCard
                title="Connexions (24h)"
                value={displayStats?.activity?.daily_logins || 0}
                icon={<span className="text-2xl">⚡</span>}
                color="text-blue-400"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <StatsCard
                title="Paiements Consultation"
                value={displayStats?.consultations?.total_count || 0}
                subtitle={`${(displayStats?.consultations?.total_amount || 0).toLocaleString()} CFA`}
                icon={<span className="text-2xl">💼</span>}
                color="text-orange-400"
              />
              <StatsCard
                title="Revenus Consultations"
                value={`${(displayStats?.consultations?.total_amount || 0).toLocaleString()} CFA`}
                subtitle={`${displayStats?.consultations?.total_count || 0} paiements`}
                icon={<span className="text-2xl">💵</span>}
                color="text-emerald-400"
              />
            </div>
          </>
        )}

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'overview'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Vue d'ensemble
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Utilisateurs
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'activities'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Activités
          </button>
          <button
            onClick={() => setActiveTab('prospects')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'prospects'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Prospects
          </button>
          <button
            onClick={() => setActiveTab('visitors')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'visitors'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Visiteurs
          </button>
          <button
            onClick={() => setActiveTab('users-creation')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'users-creation'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Créer Utilisateur
          </button>
          <button
            onClick={() => setActiveTab('balance')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'balance'
                ? 'bg-orange-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Solde & Finances
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && <UserManagement onDataChanged={fetchDashboardData} />}
        {activeTab === 'activities' && <ActivityHistory />}
        {activeTab === 'prospects' && <ProspectManagement userRole="SUPERADMIN" />}
        {activeTab === 'visitors' && (
          <div className="bg-[#1E293B] rounded-lg p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Liste des Visiteurs</h2>
              <p className="text-slate-400">Total: {visitors.length}</p>
            </div>
            
            <SearchAndSort
              data={visitors}
              searchFields={['full_name', 'phone_number', 'purpose', 'cni_number', 'registered_by', 'other_purpose']}
              sortOptions={[
                { value: 'arrival_time', label: "Date d'arrivǸe" },
                { value: 'full_name', label: 'Nom' },
                { value: 'purpose', label: 'Motif' }
              ]}
              onFilteredDataChange={handleFilteredVisitorsChange}
              placeholder="Rechercher un visiteur (nom, telephone, motif, CNI)..."
            />

            <div className="mt-6 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Arrivee</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Nom</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Telephone</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Motif</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">CNI</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Enregistre par</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Depart</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVisitors.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-slate-400">
                        Aucun visiteur trouvé
                      </td>
                    </tr>
                  ) : (
                    filteredVisitors.map((visitor) => (
                      <tr key={visitor.id} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                        <td className="py-3 px-4 text-slate-300">
                          {visitor.arrival_time ? new Date(visitor.arrival_time).toLocaleString('fr-FR') : 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-slate-300 font-medium">{visitor.full_name || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">{visitor.phone_number || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">
                          <div className="max-w-xs truncate" title={visitor.other_purpose ? `${visitor.purpose} - ${visitor.other_purpose}` : visitor.purpose}>
                            {visitor.purpose ? (
                              <>
                                {visitor.purpose}
                                {visitor.other_purpose && ` - ${visitor.other_purpose}`}
                              </>
                            ) : 'N/A'}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-300">{visitor.cni_number || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">{visitor.registered_by || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">
                          {visitor.departure_time
                            ? new Date(visitor.departure_time).toLocaleString('fr-FR')
                            : <span className="text-yellow-400">Dans les locaux</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === 'users-creation' && <HierarchicalUserCreation onUserCreated={fetchDashboardData} />}
        {activeTab === 'balance' && <BalanceMonitor />}
      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <Dialog open={showUserDetails} onOpenChange={setShowUserDetails}>
          <DialogContent className="bg-[#1E293B] border-slate-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white text-xl">Détails Utilisateur</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Avatar & Nom */}
              <div className="flex items-center space-x-4 pb-4 border-b border-slate-700">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {selectedUser.full_name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedUser.full_name}</h3>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    selectedUser.role === 'SUPERADMIN' ? 'bg-purple-500/20 text-purple-400' :
                    selectedUser.role === 'MANAGER' ? 'bg-blue-500/20 text-blue-400' :
                    selectedUser.role === 'CONSULTANT' ? 'bg-green-500/20 text-green-400' :
                    selectedUser.role === 'EMPLOYEE' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-slate-500/20 text-slate-400'
                  }`}>
                    {selectedUser.role}
                  </span>
                </div>
              </div>

              {/* Informations */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm mb-1">Email</p>
                  <p className="text-white font-medium">{selectedUser.email}</p>
                </div>

                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm mb-1">Statut</p>
                  <p className={`font-medium ${selectedUser.is_active ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedUser.is_active ? '✅ Actif' : '❌ Inactif'}
                  </p>
                </div>

                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm mb-1">Créé le</p>
                  <p className="text-white">
                    {new Date(selectedUser.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>

                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm mb-1">Dernière connexion</p>
                  <p className="text-white">
                    {selectedUser.last_login 
                      ? new Date(selectedUser.last_login).toLocaleDateString('fr-FR')
                      : 'Jamais'
                    }
                  </p>
                </div>
              </div>

              {/* Note de sécurité */}
              <div className="bg-slate-800/50 p-4 rounded-lg">
                <p className="text-slate-400 text-sm">
                  ℹ️ Les mots de passe sont envoyés directement par email aux utilisateurs lors de la création de leur compte.
                </p>
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t border-slate-700">
              <Button
                onClick={() => setShowUserDetails(false)}
                className="flex-1 bg-slate-700 hover:bg-slate-600"
              >
                Fermer
              </Button>
              {selectedUser.role !== 'SUPERADMIN' && (
                <Button
                  onClick={() => {
                    setShowUserDetails(false);
                    handleImpersonate(selectedUser.id);
                  }}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  🎭 Impersonner
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Profile Settings Overlay */}
      {showProfileSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end p-6">
          <div className="bg-[#0F172A] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-700">
            <div className="sticky top-0 bg-[#1E293B] border-b border-slate-700 p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">⚙️ Paramètres du Profil</h2>
              <button
                onClick={() => setShowProfileSettings(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <ProfileSettings user={user} onUpdate={fetchDashboardData} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;



